import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from 'recharts';
import { Megaphone, Users, Send, CheckCircle, RefreshCw, Bot, ArrowRight, Zap } from 'lucide-react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { SkeletonStat, SkeletonCard } from '../components/Skeletons';
import FluxoInicio from '../components/FluxoInicio';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#16a34a'];

export default function Dashboard() {
  const { t, idioma } = useT();
  const { temAcessoIA } = useAuth();
  const navigate = useNavigate();
  const [campanhas, setCampanhas] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [instancias, setInstancias] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [disparosPorDia, setDisparosPorDia] = useState([]);
  const [setupConcierge, setSetupConcierge] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [primeiraCarga, setPrimeiraCarga] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [rCampanhas, rContatos, rInstancias, rGrupos, rDisparos, rSetup] = await Promise.all([
        api.get('/campanha/listar'),
        api.get('/envio/contatos/listar'),
        api.get('/whatsapp/instancias').catch(() => ({ data: [] })),
        api.get('/grupo/listar').catch(() => ({ data: [] })),
        api.get('/envio/disparos-por-dia').catch(() => ({ data: [] })),
        // Estado da concierge para o caminho guiado (Fase F). `catch` devolvendo
        // null, e não um objeto vazio: sem o dado o FluxoInicio simplesmente não
        // mostra a trilha, em vez de mostrar seis etapas todas "pendentes" para
        // quem já configurou tudo.
        api.get('/ia/setup').catch(() => ({ data: null })),
      ]);
      setCampanhas(rCampanhas.data);
      setContatos(rContatos.data);
      setInstancias(Array.isArray(rInstancias.data) ? rInstancias.data : []);
      setGrupos(Array.isArray(rGrupos.data) ? rGrupos.data : []);
      setDisparosPorDia(Array.isArray(rDisparos.data) ? rDisparos.data : []);
      setSetupConcierge(rSetup.data && typeof rSetup.data === 'object' ? rSetup.data : null);
      setUltimaAtualizacao(new Date());
    } catch {} finally {
      setCarregando(false);
      setPrimeiraCarga(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useAutoRefresh(carregar, 15000);

  const total = campanhas.length;
  const finalizadas = campanhas.filter(c => c.status === 'finalizado').length;
  const processando = campanhas.filter(c => c.status === 'processando').length;
  const pausadas = campanhas.filter(c => c.status === 'pausada').length;
  const semInstancia = campanhas.filter(c => c.status === 'sem_instancia').length;
  const pendentes = campanhas.filter(c => c.status === 'pendente').length;
  const emAndamento = processando + pausadas + semInstancia + pendentes;
  const erros = campanhas.filter(c => c.status === 'erro').length;
  const campanhasEnviadas = campanhas.filter(c =>
    ['finalizado', 'processando', 'pausada'].includes(c.status)
  ).length;

  const pieData = [
    { name: t('dashboard.pie_finalizadas'), value: finalizadas },
    { name: t('dashboard.pie_andamento'), value: emAndamento },
    { name: t('dashboard.pie_erros'), value: erros },
  ].filter(d => d.value > 0);

  const barData = campanhas.slice(-7).map(c => ({
    nome: c.nome.substring(0, 12),
    status: c.status === 'finalizado' ? 1 : 0
  }));

  const cards = [
    { label: t('dashboard.card_total'),       valor: total,           icon: <Megaphone size={20} />,    cor: '#16a34a' },
    { label: t('dashboard.card_finalizadas'), valor: finalizadas,     icon: <CheckCircle size={20} />,  cor: '#22c55e' },
    { label: t('dashboard.card_andamento'),   valor: emAndamento,     icon: <Send size={20} />,         cor: '#f59e0b' },
    { label: t('dashboard.card_contatos'),    valor: contatos.length, icon: <Users size={20} />,        cor: '#3b82f6' },
  ];

  return (
    <div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      <div className="page-h">
        <div>
          <h1 className="page-h-title">{t('dashboard.titulo')}</h1>
          {ultimaAtualizacao && (
            <p className="page-h-sub">
              {t('dashboard.atualizado_as', {
                hora: ultimaAtualizacao.toLocaleTimeString(idioma === 'es' ? 'es' : 'pt-BR'),
              })}
            </p>
          )}
        </div>
        <div className="page-h-actions">
          <button onClick={carregar} disabled={carregando} className="btn btn-secondary btn-sm">
            <RefreshCw size={13} style={{ animation: carregando ? 'spin 1s linear infinite' : 'none' }} />
            {t('dashboard.atualizar')}
          </button>
        </div>
      </div>

      {/* Guia de início — oculto após o usuário dispensar ou concluir tudo */}
      {!primeiraCarga && (
        <FluxoInicio
          instanciasCount={instancias.length}
          contatosCount={contatos.length}
          gruposCount={grupos.length}
          campanhasCount={campanhas.length}
          campanhasEnviadas={campanhasEnviadas}
          setupConcierge={setupConcierge}
        />
      )}

      {/* Banner Concierge IA */}
      {!primeiraCarga && (
        <div style={{
          background: temAcessoIA
            ? 'linear-gradient(135deg, #0f172a 0%, #13305f 100%)'
            : 'linear-gradient(135deg, #13305f 0%, #2d1b69 100%)',
          borderRadius: '14px',
          padding: '1.5rem 2rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          flexWrap: 'wrap',
          border: '1px solid rgba(22, 163, 74,0.3)',
        }}>
          <div style={{
            background: 'rgba(22, 163, 74,0.15)',
            borderRadius: '12px',
            padding: '12px',
            flexShrink: 0,
          }}>
            <Bot size={28} color="#4ade80" />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            {temAcessoIA ? (
              <>
                <div style={{ fontWeight: 700, fontSize: '15px', color: '#fff', marginBottom: '4px' }}>
                  Concierge IA — atende, agenda e lembra por você 24h
                </div>
                <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.5 }}>
                  Configure sua concierge: ela responde o paciente na hora, agenda direto na sua agenda, lembra antes da consulta e avisa a recepção quando o caso está pronto para fechar.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700, fontSize: '15px', color: '#fff', marginBottom: '4px' }}>
                  Você está perdendo paciente fora do horário comercial
                </div>
                <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.5 }}>
                  A Concierge IA responde seus pacientes às 22h, no fim de semana, no feriado — antes da clínica do lado. Disponível a partir do plano Essencial.
                </div>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexShrink: 0, flexWrap: 'wrap' }}>
            {temAcessoIA ? (
              <button
                onClick={() => navigate('/ia')}
                style={{
                  background: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Zap size={14} /> Configurar agora
              </button>
            ) : (
              <button
                onClick={() => navigate('/creditos')}
                style={{
                  background: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <ArrowRight size={14} /> Ver planos
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid-stats">
        {primeiraCarga
          ? Array.from({ length: 4 }).map((_, i) => <SkeletonStat key={i} />)
          : cards.map(({ label, valor, icon, cor }) => (
              <div key={label} className="stat" style={{ flexDirection: 'row', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  background: cor + '1f', borderRadius: '12px', padding: '10px', color: cor,
                  display: 'grid', placeItems: 'center'
                }}>
                  {icon}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className="stat-value">{valor}</div>
                  <div className="stat-label">{label}</div>
                </div>
              </div>
            ))
        }
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
        {primeiraCarga ? (
          <>
            <SkeletonCard height={260} />
            <SkeletonCard height={260} />
          </>
        ) : (
          <>
            <div className="card">
              <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 1rem' }}>{t('dashboard.ultimas_campanhas')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData}>
                  <XAxis dataKey="nome" tick={{ fontSize: 11, fill: '#8a8f9f' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#8a8f9f' }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, border: '1px solid #e6e8ef', fontSize: 12 }}
                    cursor={{ fill: 'rgba(22, 163, 74,0.06)' }}
                  />
                  <Bar dataKey="status" fill="#16a34a" radius={[6, 6, 0, 0]} name={t('dashboard.bar_finalizada')} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 1rem' }}>{t('dashboard.status_campanhas')}</h3>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e6e8ef', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty" style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {t('dashboard.nenhuma_campanha')}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Disparos por dia — últimos 30 dias */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 1rem' }}>
          {t('dashboard.disparos_por_dia') || 'Disparos por dia (últimos 30 dias)'}
        </h3>
        {disparosPorDia.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={disparosPorDia}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" />
              <XAxis dataKey="dia" tick={{ fontSize: 11, fill: '#8a8f9f' }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#8a8f9f' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e6e8ef', fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="enviados" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} name="Enviados" />
              <Line type="monotone" dataKey="erros"    stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="Erros" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="empty" style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {t('dashboard.nenhum_disparo') || 'Sem disparos nos últimos 30 dias'}
          </div>
        )}
      </div>
    </div>
  );
}
